import {
  ClientConfig,
  Ap_totesPlacement,
  Inv_InventoryDateLabelSettings,
  AS_HomepageType
} from "./clientConfig";

/**
 * UI Metadata for client configuration fields
 * This is the single source of truth for field rendering and default values
 */

export type FieldType =
  | "boolean"
  | "select"
  | "text"
  | "array"
  | "object"
  | "keyValue"
  | "chips"
  | "multiChips";

export interface FieldMetadata {
  type: FieldType;
  label: string;
  description: string;
  section: string;
  defaultValue: unknown;
  options?: readonly string[]; // For select and chips fields
  hidden?: boolean; // Don't show in UI
}

export type ClientConfigMetadata = {
  [K in keyof Required<ClientConfig>]: FieldMetadata;
};

const defaultHomepageTypes: AS_HomepageType[] = [
  "picking",
  "induction",
  "inventory holds",
  "cycle counts",
  "inventory"
];

export const clientConfigFieldMetadata: ClientConfigMetadata = {
  // ============= SYSTEM =============
  configVersion: {
    type: "text",
    label: "Config Version",
    description:
      "Configuration schema version. Increment when adding/removing fields.",
    section: "System",
    defaultValue: 1,
    hidden: true
  },

  // ============= ANDON =============
  an_andonFeaturesEnabled: {
    type: "boolean",
    label: "Andon Features Enabled",
    description: "Enable andon board and alert features",
    section: "Andon",
    defaultValue: true
  },
  an_boardLayouts: {
    type: "object",
    label: "Board Layouts",
    description:
      "Andon board layout configurations (requires JSON editing for complex structure)",
    section: "Andon",
    defaultValue: [],
    hidden: true
  },
  an_isHandRaiseTypePickerEnabled: {
    type: "boolean",
    label: "Hand Raise Type Picker",
    description:
      "Enable request type selection modal when raising hand (general/blocker)",
    section: "Andon",
    defaultValue: false
  },

  // ============= AUTOSTORE PICKING =============
  ap_totesPlacement: {
    type: "chips",
    label: "Totes Placement",
    description: "Where totes are placed during picking",
    section: "Autostore Picking",
    defaultValue: "split" as Ap_totesPlacement,
    options: ["split", "right"] as readonly Ap_totesPlacement[]
  },
  ap_prepTotesModalEnabled: {
    type: "boolean",
    label: "Prep Totes Modal Enabled",
    description: "Show a modal for preparing totes before picking starts",
    section: "Autostore Picking",
    defaultValue: false
  },
  ap_confirmCartNumberEnabled: {
    type: "boolean",
    label: "Confirm Cart Number Enabled",
    description:
      "Require confirmation of cart number during picking operations",
    section: "Autostore Picking",
    defaultValue: false
  },
  ap_fusionPortScreenEnabled: {
    type: "boolean",
    label: "Fusion Port Screen Enabled",
    description: "Enable fusion port screen interface for picking",
    section: "Autostore Picking",
    defaultValue: false
  },
  ap_prepTotesModalShowTitle: {
    type: "boolean",
    label: "Prep Totes Modal - Show Title",
    description: "Display title in the prep totes modal",
    section: "Autostore Picking",
    defaultValue: false
  },
  ap_prepTotesModalShowInstructionText: {
    type: "boolean",
    label: "Prep Totes Modal - Show Instruction Text",
    description: "Display instruction text in the prep totes modal",
    section: "Autostore Picking",
    defaultValue: true
  },
  ap_prepTotesModalColumnPlacement: {
    type: "boolean",
    label: "Prep Totes Modal - Column Placement",
    description: "Use column-based placement in the prep totes modal",
    section: "Autostore Picking",
    defaultValue: false
  },
  ap_prepTotesModalShowToteId: {
    type: "boolean",
    label: "Prep Totes Modal - Show Tote ID",
    description: "Display tote ID in the prep totes modal",
    section: "Autostore Picking",
    defaultValue: true
  },
  ap_prepTotesModalShowCustomerName: {
    type: "boolean",
    label: "Prep Totes Modal - Show Customer Name",
    description: "Display customer name in the prep totes modal",
    section: "Autostore Picking",
    defaultValue: true
  },
  ap_prepTotesModalShowToteSize: {
    type: "boolean",
    label: "Prep Totes Modal - Show Tote Size",
    description: "Display tote size information in the prep totes modal",
    section: "Autostore Picking",
    defaultValue: true
  },
  ap_scanningIndicatorShown: {
    type: "boolean",
    label: "Scanning Indicator Shown",
    description: "Show visual scanning indicator during picking operations",
    section: "Autostore Picking",
    defaultValue: false
  },
  ap_addToteFunctionalityEnabled: {
    type: "boolean",
    label: "Add Tote Functionality Enabled",
    description: "Allow adding new totes during picking operations",
    section: "Autostore Picking",
    defaultValue: false
  },
  ap_repickToteFunctionalityEnabled: {
    type: "boolean",
    label: "Repick Tote Functionality Enabled",
    description: "Allow repicking items into different totes",
    section: "Autostore Picking",
    defaultValue: false
  },
  ap_excludeRecalledHold: {
    type: "boolean",
    label: "Exclude Recalled Hold",
    description:
      "Exclude the Recalled hold from hold dropdowns. Also used for Inventory.",
    section: "Autostore Picking",
    defaultValue: false
  },
  ap_includeDirtyBinHold: {
    type: "boolean",
    label: "Include Dirty Bin Flag",
    description: "Include the Dirty Bin flag on hold dropdowns",
    section: "Autostore Picking",
    defaultValue: false
  },
  ap_includeDamagedBinHold: {
    type: "boolean",
    label: "Include Damaged Bin Flag",
    description: "Include the Damaged Bin flag on hold dropdowns",
    section: "Autostore Picking",
    defaultValue: false
  },
  ap_includeMisconfiguredBinHold: {
    type: "boolean",
    label: "Include Misconfigured Bin Flag",
    description: "Include the Misconfigured Bin flag on hold dropdowns",
    section: "Autostore Picking",
    defaultValue: false
  },
  ap_applyingLabelProcessEnabled: {
    type: "boolean",
    label: "Applying Label Process Enabled",
    description: "Enable the label application process during picking",
    section: "Autostore Picking",
    defaultValue: false
  },
  ap_showPickQtyWhileBinLoaderIsActive: {
    type: "boolean",
    label: "Show Pick Qty While Bin Loader Is Active",
    description:
      "Display pick quantity information while bin loader is running",
    section: "Autostore Picking",
    defaultValue: true
  },
  ap_isTopAreaOfToteClickable: {
    type: "boolean",
    label: "Top Area of Tote Clickable",
    description: "Allow clicking on the top area of totes for interaction",
    section: "Autostore Picking",
    defaultValue: true
  },
  ap_showOrderOrProductCancellationModal: {
    type: "boolean",
    label: "Show Order/Product Cancellation Modal",
    description: "Display modal for order or product cancellation options",
    section: "Autostore Picking",
    defaultValue: false
  },

  // ============= BATCHES =============
  bat_pickFromBatchesEnabled: {
    type: "boolean",
    label: "Pick From Batches Enabled",
    description: "Allow picking items from batch groups",
    section: "Batches",
    defaultValue: true
  },
  bat_stageFromBatchesEnabled: {
    type: "boolean",
    label: "Stage From Batches Enabled",
    description: "Allow staging items from batch groups",
    section: "Batches",
    defaultValue: true
  },

  // ============= BIN CLEANING =============
  binCleaningEnabled: {
    type: "boolean",
    label: "Bin Cleaning Enabled",
    description: "Enable bin cleaning and maintenance features",
    section: "Bin Cleaning",
    defaultValue: false
  },
  ap_binRemovedFlow: {
    type: "boolean",
    label: "Bin Removal",
    description: "Enable workflow for handling bin removal",
    section: "Bin Cleaning",
    defaultValue: false
  },

  // ============= DEVELOPER =============
  dev_orderCreation: {
    type: "boolean",
    label: "Order Creation",
    description: "Enable order creation in dev mode",
    section: "Developer",
    defaultValue: false
  },
  dev_useV2OrderCreation: {
    type: "boolean",
    label: "Use V2 Order Creation",
    description: "Use version 2 of the order creation interface",
    section: "Developer",
    defaultValue: false
  },
  dev_ptlSimulation: {
    type: "boolean",
    label: "PTL Simulation",
    description: "Enable pick-to-light simulation",
    section: "Developer",
    defaultValue: false
  },
  dev_conveyanceSimulation: {
    type: "boolean",
    label: "Conveyance Simulation",
    description: "Enable conveyance system simulation for testing",
    section: "Developer",
    defaultValue: false
  },

  // ============= INVENTORY =============
  inv_uomOptions: {
    type: "keyValue",
    label: "UOM Options",
    description: "Unit of measure options (value/label pairs for dropdown)",
    section: "Inventory",
    defaultValue: [{ value: "ea", label: "ea" }]
  },
  inv_addSetExpirationEnabled: {
    type: "boolean",
    label: "Set Expiration Enabled",
    description: "Allow setting expiration dates when adding inventory",
    section: "Inventory",
    defaultValue: true
  },
  inv_addUomSelectionForASBinsDisabled: {
    type: "boolean",
    label: "UOM Selection for AS Bins Disabled",
    description: "Disable unit of measure selection for AutoStore bins",
    section: "Inventory",
    defaultValue: true
  },
  inv_addUseUomFromFC: {
    type: "boolean",
    label: "Use UOM From FC",
    description: "Use unit of measure settings from fulfillment center",
    section: "Inventory",
    defaultValue: false
  },
  inv_inventoryDateLabel: {
    type: "chips",
    label: "Inventory Date Label",
    description:
      "Label type to use for inventory dates (expiration or manufacture)",
    section: "Inventory",
    defaultValue: "expiration" as Inv_InventoryDateLabelSettings,
    options: [
      "expiration",
      "manufacture"
    ] as readonly Inv_InventoryDateLabelSettings[]
  },
  inv_inventoryDateRequired: {
    type: "boolean",
    label: "Inventory Date Required",
    description: "Make inventory date entry mandatory for all items",
    section: "Inventory",
    defaultValue: false
  },

  // ============= OPERATIONS =============
  manualOpsEnabled: {
    type: "boolean",
    label: "Manual Operations Enabled",
    description: "Enable manual operations and override capabilities",
    section: "Operations",
    defaultValue: false
  },
  cartAssignmentEnabled: {
    type: "boolean",
    label: "Cart Assignment Enabled",
    description: "Enable assignment of carts to workers or tasks",
    section: "Operations",
    defaultValue: true
  },
  cartPrepEnabled: {
    type: "boolean",
    label: "Cart Prep Enabled",
    description: "Enable cart preparation workflow before picking",
    section: "Operations",
    defaultValue: true
  },

  // ============= ORDERS =============
  ord_includeSpokes: {
    type: "boolean",
    label: "Include Spokes",
    description: "Include spoke locations in order processing",
    section: "Orders",
    defaultValue: true
  },

  // ============= PUTAWAY =============
  putaway_multipleSearchTerms: {
    type: "boolean",
    label: "Multiple Search Terms",
    description: "Allow using multiple search terms for putaway operations",
    section: "Putaway",
    defaultValue: false
  },
  putaway_showScanFilters: {
    type: "boolean",
    label: "Show Scan Filters",
    description: "Display scan filter options in putaway interface",
    section: "Putaway",
    defaultValue: true
  },
  putaway_showExceptionsButton: {
    type: "boolean",
    label: "Show Exceptions Button",
    description: "Display exceptions button for handling putaway issues",
    section: "Putaway",
    defaultValue: false
  },

  // ============= WORKSTATION =============
  ws_addRemovePortsEnabled: {
    type: "boolean",
    label: "Add/Remove Ports Enabled",
    description:
      "Allow adding and removing ports from workstation configuration",
    section: "Workstation",
    defaultValue: true
  },
  ws_modifyPortSetupEnabled: {
    type: "boolean",
    label: "Modify Port Setup Enabled",
    description: "Enable modification of port setup and configuration",
    section: "Workstation",
    defaultValue: false
  },
  ws_deleteWorkstationEnabled: {
    type: "boolean",
    label: "Delete Workstation Enabled",
    description: "Allow deletion of workstation configurations",
    section: "Workstation",
    defaultValue: true
  },

  // ============= SITE =============
  site_defaultLanguageCode: {
    type: "chips",
    label: "Default Language Code",
    description: "Default language code for the site interface",
    section: "Site",
    defaultValue: "en",
    options: ["en", "es", "fr", "de", "bs", "no", "sv"] as const
  },

  // ============= FULFILLMENT CENTER =============
  fc_autostore_homepages: {
    type: "multiChips",
    label: "Autostore Homepages",
    description: "Toggle available homepage options for autostore workers",
    section: "Fulfillment Center",
    defaultValue: defaultHomepageTypes,
    options: [
      "picking",
      "induction",
      "induction v2",
      "inventory holds",
      "cycle counts",
      "cycle counts v2",
      "inventory"
    ] as const
  }
};

/**
 * Derive default ClientConfig from metadata
 * This ensures metadata is the single source of truth
 */
export function deriveDefaultConfigFromMetadata(): ClientConfig {
  const config: Record<string, unknown> = {};

  for (const [key, metadata] of Object.entries(clientConfigFieldMetadata)) {
    config[key] = metadata.defaultValue;
  }

  return config as ClientConfig;
}

/**
 * Get sections grouped by name
 */
export function getConfigSections(): Map<string, (keyof ClientConfig)[]> {
  const sections = new Map<string, (keyof ClientConfig)[]>();

  for (const [key, metadata] of Object.entries(clientConfigFieldMetadata)) {
    const section = metadata.section;
    if (!sections.has(section)) {
      sections.set(section, []);
    }
    sections.get(section)!.push(key as keyof ClientConfig);
  }

  return sections;
}

/**
 * Get visible fields (exclude hidden ones)
 */
export function getVisibleFields(): (keyof ClientConfig)[] {
  return Object.entries(clientConfigFieldMetadata)
    .filter(([_, metadata]) => !metadata.hidden)
    .map(([key]) => key as keyof ClientConfig);
}
